import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useApi } from '../hooks/useApi';

export interface MeDto {
  id: number;
  email: string;
  displayName: string | null;
  isSuspended: boolean;
  isAdmin: boolean;
  isOrgManager: boolean;
  entitlements: { marketIds: number[]; neighborhoodIds: number[] };
  organization: { id: number; name: string; logoUrl: string | null; isApproved: boolean; activeMemberCount: number } | null;
  hasPaidSubscription: boolean;
  photoUrl: string | null;
}

export interface OrgCapacityError {
  organizationName: string;
  managerEmail: string | null;
}

interface UserContextValue {
  me: MeDto | null;
  loading: boolean;
  showPdfLens: boolean;
  needsOrgSetup: boolean;
  orgCapacityError: OrgCapacityError | null;
  refresh: () => void;
}

const UserContext = createContext<UserContextValue>({
  me: null,
  loading: true,
  showPdfLens: false,
  needsOrgSetup: false,
  orgCapacityError: null,
  refresh: () => {},
});

export function useUserContext() {
  return useContext(UserContext);
}

export function UserProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth0();
  const { authFetch } = useApi();
  const [me, setMe] = useState<MeDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [orgCapacityError, setOrgCapacityError] = useState<OrgCapacityError | null>(null);

  const fetchMe = useCallback(() => {
    if (!isAuthenticated) { setMe(null); setOrgCapacityError(null); setLoading(false); return; }
    setLoading(true);
    authFetch('/api/me')
      .then(async r => {
        if (r.status === 403) {
          const body = await r.json().catch(() => null);
          if (body && typeof body.organizationName === 'string') {
            setOrgCapacityError({ organizationName: body.organizationName, managerEmail: body.managerEmail ?? null });
            setMe(null);
            setLoading(false);
            return;
          }
        }
        const data: MeDto = await r.json();
        setOrgCapacityError(null);
        setMe(data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchMe(); }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  const showPdfLens = !!(me?.hasPaidSubscription || me?.isAdmin);
  const needsOrgSetup = !!(me?.isOrgManager && me.organization && me.organization.name === '');

  return (
    <UserContext.Provider value={{ me, loading, showPdfLens, needsOrgSetup, orgCapacityError, refresh: fetchMe }}>
      {children}
    </UserContext.Provider>
  );
}
